/**
 * Builds the playable variant of a source round for a difficulty level.
 * Family-set rounds are authored for Family (easy); adult-set rounds for Classic (medium).
 */
import { journeyOrder } from "./route";
import type { Level, NextRound, PinRound, Round, WhoRound, Region } from "../puzzles/types";
import { NEXT_DEFAULT_SCALE_KM, pinScaleKm } from "./scoring";
import { shuffle } from "./random";

export type Playable =
  | (Omit<PinRound, "hints"> & { level: Level; scale: number; cities: boolean; region: Region | null; hints: string[] })
  | (NextRound & { level: Level; scale: number; cities: boolean; hint: string | null })
  | (WhoRound & { level: Level; choices: string[] | null; hideYears: boolean });

export function derive(source: Round, level: Level, seed: number): Playable {
  const p = structuredClone(source);
  const easy = level === "easy";
  switch (p.type) {
    case "pin":
      return {
        ...p,
        level,
        scale: pinScaleKm(p.scaleKm, level),
        cities: easy,
        region: easy ? (p.easy?.region ?? null) : null,
        // Hints cost a step each (DECISIONS 2026-10-03). A Family day not yet migrated: its old free hint becomes hint 1.
        hints: p.hints ? [...p.hints] : easy && p.easy?.hint ? [p.easy.hint] : [],
      };
    case "next":
      // Drop a pin where the journey went next, scored on the distance table (DECISIONS 2026-09-30).
      return { ...p, level, scale: p.scaleKm ?? NEXT_DEFAULT_SCALE_KM, cities: easy, hint: easy ? (p.easy?.hint ?? null) : null };
    case "who":
      return {
        ...p,
        // Places are shown oldest first (DECISIONS 2026-09-29). Display order only: the authored
        // order stays the source of the Knowledge Map fact IDs, which are locked once published.
        clues: journeyOrder(p.clues).stops,
        level,
        choices: easy && p.decoys ? shuffle([p.answer, ...p.decoys], seed) : null,
        hideYears: level === "hard",
      };
  }
}
